<template>
	<base-layout>
    <template v-slot:header>
      <a :href="`/broadcasts/${broadcast.id}`" class="p-2 text-base text-red-500">
        ‹ See Broadcast Details
      </a> 
      <div class="mt-1 mb-2 md:flex md:items-center md:justify-between">
        <div class="flex-1 min-w-0 mt-2 ">
          <h3 class='text-gray-400 text-xl leading-9 sm:truncate tracking-tight'>
            {{ broadcast.broadcastListName }} Broadcast, {{ broadcast.dateAsString }}
          </h3>
          <h1 class="font-bold text-gray-900 text-2xl sm:text-3xl leading-9 sm:truncate tracking-tight">
            {{ broadcast.subject }}</h1>
        </div>
      </div>

    </template>
    <template v-slot:main>
      <div class="bg-white rounded shadow px-4">

        <div v-if="user" class="py-6">
          <template>
            <div class="space-y-4">

              <!-- View Toggle -->
              <div class="flex flex-row-reverse gap-2">
                <button @click="viewMode = 'grid'" :class="viewMode === 'grid' ? activeBtn : inactiveBtn">
                  <svg width="20px" height="20px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <title/>
                    <g id="Complete">
                      <g id="grid">
                        <g>
                          <rect fill="none" height="7" stroke="#000000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" width="7" x="14.5" y="2.5"/>
                          <rect fill="none" height="7" stroke="#000000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" width="7" x="14.5" y="14.5"/>\
                          <rect fill="none" height="7" stroke="#000000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" width="7" x="2.5" y="2.5"/>
                          <rect fill="none" height="7" stroke="#000000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" width="7" x="2.5" y="14.5"/>
                        </g>
                      </g>
                    </g>
                  </svg>
                </button>
                <button @click="viewMode = 'carousel'" :class="viewMode === 'carousel' ? activeBtn : inactiveBtn">
                  <svg fill="#000000" width="20px" height="20px" viewBox="0 0 24 24">
                  <path d="M4 19h2c0 1.103.897 2 2 2h8c1.103 0 2-.897 2-2h2c1.103 0 2-.897 2-2V7c0-1.103-.897-2-2-2h-2c0-1.103-.897-2-2-2H8c-1.103 0-2 .897-2 2H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2zM20 7v10h-2V7h2zM8 5h8l.001 14H8V5zM4 7h2v10H4V7z"/></svg>
                </button>
              </div>

              <!-- GRID VIEW -->
              <div v-if="viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" >

                <!-- SKELETON -->
                <template v-if="isLoading">
                  <div 
                    v-for="n in 3"
                    :key="'skeleton-' + n"
                    class="h-96 w-full bg-gray-200 rounded-lg animate-pulse"
                  ></div>
                </template>

                <!-- IMAGES -->
                <template v-else>
                  <img
                    v-for="(img, index) in broadcastImages"
                    :key="index"
                    :src="imageSrc(img)"
                    class="w-full max-w-[200px] h-[400px] max-h-[500px] object-contain rounded-lg border shadow-sm"
                  />
                </template>

              </div>

              <!-- CAROUSEL VIEW -->
              <div v-else class="relative overflow-hidden w-full">
                <!-- Slides -->
                <div class="flex transition-transform duration-500 ease-out"
                    :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
                  <div
                    v-for="(img, index) in broadcastImages"
                    :key="index"
                    class="min-w-full flex justify-center items-center">
                    <img
                      :src="imageSrc(img)"
                      class="w-auto h-96 max-h-[500px] object-contain rounded-lg"
                    />
                  </div>
                </div>


                <!-- Prev -->
                <button @click="prev"
                  class="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-red-500 hover:text-white rounded-lg p-2 shadow px-4">
                  ‹
                </button>

                <!-- Next -->
                <button @click="next"
                  class="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-red-500 hover:text-white rounded-lg p-2 shadow px-4">
                  ›
                </button>

                <!-- Dots -->
                <div class="flex justify-center gap-2 mt-3">
                  <span
                    v-for="(img, index) in broadcastImages"
                    :key="index"
                    @click="currentIndex = index"
                    class="w-2 h-2 rounded-full cursor-pointer"
                    :class="index === currentIndex ? 'bg-gray-800' : 'bg-gray-400'"
                  />
                </div>
              </div>

            </div>
          </template>
        </div>

      </div>
    </template>
  </base-layout>
</template>

<script>

import { mapState } from 'vuex'
import BaseLayout from '@/components/BaseLayout.vue'
import eventBus from '../utils/eventBus'

import store from '../store'
import axios from 'axios'

// const _ = require('lodash');

export default {
  name: 'BroadcastPictures',
  components: {
    BaseLayout,
  },
  async beforeRouteEnter(to, from, next) {
    try {
      const resp = await store.dispatch('getBroadcast', to.params.id)
      if (resp.status !== 200) {
        eventBus.$emit("createSnackbar", {
          type: "error",
          title: "Broadcast removed",
          body: resp.msg,
          timeoutInMS: 5000,
        })
        return;
      }
      next(vm => {
        vm.broadcast = resp.broadcast
        const subject = vm.broadcast.subject.length > 60 
          ? vm.broadcast.subject.substring(0, 57) + '...' 
          : vm.broadcast.subject;
        document.title = `${subject} - The Broker Site`
      })
    } catch (e) {
      console.log("err", e)
    }
  },
  created() {
    this.broadcastId = this.$route.params.id;
    this.fetchImages();
  },
  data() {
    return {
      broadcast: null,
      broadcastId: null,
      broadcastImages: [],
      isLoading: true,
      viewMode: 'grid',
      currentIndex: 0,
      activeBtn: 'px-4 py-2 rounded bg-gray-200 text-white text-sm',
      inactiveBtn: 'px-4 py-2 rounded text-gray-700 border border-gray-200 text-sm'
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKey)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKey)
  },
  computed: {
    ...mapState(["route", 'user']),
  },

  methods: {
    async fetchImages() {
      const response = await axios.get(`/api/broadcasts/fetchimages/${this.broadcastId}`);
      this.broadcastImages = response.data.images;
      this.isLoading = false;
    },

    imageSrc(img) {
      return `https://www.thebrokersite.com/images/broadcast/${this.broadcastId}/${img}`;
    },
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.broadcastImages.length;
    },
    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.broadcastImages.length) %
        this.broadcastImages.length;
    },
    handleKey(e) {
      if (this.viewMode !== 'carousel') return;

      if (e.key === 'ArrowRight') {
        this.next();
      }

      if (e.key === 'ArrowLeft') {
        this.prev();
      }
    }
  },
}
</script>
